"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  Suspense,
} from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  getAdditionalUserInfo,
  type User,
} from "firebase/auth";
import { useRouter } from "next/navigation";
import SearchParamsObserver from "@/components/shared/SearchParamsObserver";
import { auth, googleProvider } from "@/lib/firebase";
import { maybeSignInForE2E } from "@/lib/e2e-auth";
import { AuthAPI } from "@/lib/api";
import { EXTENSION_ID, getExtensionIdFromURL, sendTokenToExtension } from "@/lib/extension";
import {
  AuthState,
  FirebaseAuthErrorCode,
  type AuthContext as AuthContextType,
  isFirebaseAuthError,
} from "@/types/auth";
import posthog from "posthog-js";
import { toast } from "sonner";

/**
 * Authentication Context
 */
const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Authentication Provider Props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Get user-friendly error message from Firebase Auth error
 */
function getAuthErrorMessage(error: unknown): string {
  if (!isFirebaseAuthError(error)) {
    return "An unexpected error occurred. Please try again.";
  }

  switch (error.code) {
    case FirebaseAuthErrorCode.POPUP_CLOSED:
      return "Sign-in was cancelled. Please try again.";

    case FirebaseAuthErrorCode.ACCOUNT_EXISTS:
      return "An account already exists with this email using a different sign-in method.";

    case FirebaseAuthErrorCode.NETWORK_REQUEST_FAILED:
      return "Network error. Please check your connection and try again.";

    case FirebaseAuthErrorCode.TOO_MANY_REQUESTS:
      return "Too many sign-in attempts. Please wait a moment and try again.";

    case FirebaseAuthErrorCode.USER_CANCELLED:
      return "Sign-in was cancelled.";

    case FirebaseAuthErrorCode.UNAUTHORIZED_DOMAIN:
      return "This domain is not authorized for sign-in. Please contact support.";

    default:
      console.error("Unknown Firebase Auth error:", error);
      return `Authentication failed: ${error.message || "Unknown error"}`;
  }
}

/**
 * Authentication Provider Component
 */
export function AuthProvider({
  children,
}: AuthProviderProps): React.ReactElement {
  // State
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<AuthState>(AuthState.IDLE);
  const [isNewUser, setIsNewUser] = useState(false);
  const googleSignInPending = useRef(false);
  const backendReadyUid = useRef<string | null>(null);
  const backendFailedUid = useRef<string | null>(null);
  const backendVerification = useRef<{
    uid: string;
    request: Promise<string>;
  } | null>(null);

  // Hooks
  const router = useRouter();
  const [queryString, setQueryString] = useState("");

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const verifyBackendUser = useCallback((firebaseUser: User) => {
    if (backendVerification.current?.uid === firebaseUser.uid) {
      return backendVerification.current.request;
    }
    const request = firebaseUser.getIdToken(true)
      .then(idToken => new AuthAPI().verifyToken(idToken));
    backendVerification.current = { uid: firebaseUser.uid, request };
    return request;
  }, []);

  /**
   * Handle authentication process after successful Firebase sign-in (only for new logins)
   */
  const handleNewUserLogin = useCallback(
    async (firebaseUser: User): Promise<boolean> => {

      const redirectedFromExtension = getExtensionIdFromURL(new URLSearchParams(queryString)) === EXTENSION_ID;

      try {
        setState(AuthState.VERIFYING);
        setError(null);

        const customToken = await verifyBackendUser(firebaseUser);
        if (auth.currentUser?.uid !== firebaseUser.uid) {
          throw new Error("The signed-in account changed. Please try again.");
        }
        backendReadyUid.current = firebaseUser.uid;

        if (redirectedFromExtension) {
          posthog.capture("extension_auth_success", {
            redirectedFromExtension: true,
          });

          // Send token to extension
          setState(AuthState.SENDING_TO_EXTENSION);

          return await new Promise<boolean>((resolve, reject) => {
            sendTokenToExtension(
              EXTENSION_ID,
              customToken,
              () => {
                setState(AuthState.AUTHENTICATED);
                toast.success("Successfully connected to FillApp extension!");
                router.push("/get-started");
                resolve(true);
              },
              (errorMessage) => {
                console.error(
                  "Failed to send token to extension:",
                  errorMessage
                );
                toast.error(`Failed to connect to extension: ${errorMessage}`);
                setState(AuthState.ERROR);
                reject(new Error(errorMessage));
              },
              { closeTab: false }
            );
          });
        } else {
          // No extension, redirect to app (only for new logins)
          setState(AuthState.AUTHENTICATED);
          return false;
        }
      } catch (error) {
        console.error("New user login error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Authentication failed";
        toast.error(errorMessage);
        setError(errorMessage);
        setState(AuthState.ERROR);
        throw error;
      }
    },
    [queryString, router, verifyBackendUser]
  );

  /**
   * Handle existing user state restoration (no redirects)
   */
  const handleExistingUserState = useCallback(async (firebaseUser: User) => {
    setUser(null);
    setLoading(true);
    setState(AuthState.VERIFYING);
    const isCurrent = () => auth.currentUser?.uid === firebaseUser.uid &&
      !googleSignInPending.current;
    try {
      await verifyBackendUser(firebaseUser);
      if (!isCurrent()) return;
      backendReadyUid.current = firebaseUser.uid;
      setUser(firebaseUser);
      setError(null);
      setState(AuthState.AUTHENTICATED);
      posthog.identify(firebaseUser.uid, {
        email: firebaseUser.email,
        name: firebaseUser.displayName,
        authProvider: "google",
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (backendFailedUid.current === firebaseUser.uid) return;
      backendFailedUid.current = firebaseUser.uid;
      setError(error instanceof Error ? error.message : "Authentication failed");
      setState(AuthState.ERROR);
      toast.error("Could not finish signing in. Please try again.");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [verifyBackendUser]);

  /**
   * Sign in with Google
   */
  const signInWithGoogle = useCallback(async () => {
    if (googleSignInPending.current) return;
    googleSignInPending.current = true;
    backendVerification.current = null;
    backendFailedUid.current = null;
    let attemptedUser: User | null = null;
    try {
      setLoading(true);
      setState(AuthState.GOOGLE_SIGNIN);
      setError(null);

      const result = await signInWithPopup(auth, googleProvider);

      if (result.user) {
        attemptedUser = result.user;
        const redirectedFromExtension = getExtensionIdFromURL(new URLSearchParams(queryString)) === EXTENSION_ID;
        const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;

        // Handle the authentication flow for new login (extension redirect, token verification)
        const redirected = await handleNewUserLogin(result.user);

        // Identify user in PostHog for all subsequent tracking
        posthog.identify(result.user.uid, {
          email: result.user.email,
          name: result.user.displayName,
          isNewUser,
          authProvider: "google",
          createdAt: result.user.metadata.creationTime,
        });

        if (isNewUser) {
          posthog.capture("user_signup", {
            redirectedFromExtension,
            authProvider: "google",
          });
        } else {
          posthog.capture("user_signin", {
            redirectedFromExtension,
            authProvider: "google",
          });
        }

        // For new web users (non-extension), redirect to get-started demo page
        if (!redirected && isNewUser) {
          setIsNewUser(true);
          setUser(result.user);
          router.push("/get-started");
          return;
        }

        setUser(result.user);
      }
    } catch (error) {
      console.error("Google sign-in error:", error);
      if (auth.currentUser?.uid === attemptedUser?.uid && auth.currentUser &&
          backendReadyUid.current !== auth.currentUser.uid) {
        backendFailedUid.current = auth.currentUser.uid;
      }
      const errorMessage = getAuthErrorMessage(error);
      toast.error(errorMessage);
      setState(AuthState.ERROR);
    } finally {
      googleSignInPending.current = false;
      setLoading(false);
    }
  }, [handleNewUserLogin, queryString, router]);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      await firebaseSignOut(auth);
      backendReadyUid.current = null;
      backendFailedUid.current = null;
      backendVerification.current = null;

      posthog.capture("user_signout");
      // Reset PostHog identification
      posthog.reset();

      setUser(null);
      setState(AuthState.IDLE);
      toast.success("Successfully signed out!");
      router.push("/");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [router]);

  /** Dev/test-only: sign in with a planted custom token (no-op in prod). */
  useEffect(() => {
    maybeSignInForE2E();
  }, []);

  /**
   * Listen for authentication state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser: User | null) => {
        // Firebase publishes the user before verifyToken provisions the profile.
        if (googleSignInPending.current && firebaseUser) return;
        if (firebaseUser && backendFailedUid.current === firebaseUser.uid) {
          setLoading(false);
          return;
        }
        if (firebaseUser) {
          if (backendReadyUid.current === firebaseUser.uid) {
            setUser(firebaseUser);
            setState(AuthState.AUTHENTICATED);
            setLoading(false);
          } else {
            void handleExistingUserState(firebaseUser);
          }
        } else {
          setUser(null);
          backendReadyUid.current = null;
          backendFailedUid.current = null;
          backendVerification.current = null;
          if (!googleSignInPending.current) {
            setState(AuthState.IDLE);
            setLoading(false);
          }
        }
      }
    );

    return unsubscribe;
  }, [handleExistingUserState]);

  /**
   * Context value
   */
  const contextValue: AuthContextType = {
    // State
    user,
    loading,
    error,
    state,
    isNewUser,

    // Actions
    signInWithGoogle,
    signOut,
    clearError,
    handleNewUserLogin,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <Suspense fallback={null}>
        <SearchParamsObserver onChange={setQueryString} />
      </Suspense>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use authentication context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export default AuthContext;
